package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** Acceptance, approval and export are separate steps, never an automatic release promise. */
@Composable
fun LoopBand() {
    Band(ground = BandGround.Paper, labelledBy = "loop-title") {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            BandLabel(t("marketing.loop_band.how_it_works", "How it works"))
            BandHeading(t("marketing.loop_band.source_to_reviewed_translation", "From source strings to reviewed translations."), id = "loop-title")
        }
        Ol(LoopPipelineStyle.toModifier().toAttrs { attr("role", "list") }) {
            WorkflowStage(1, t("marketing.loop_band.bring_sources", "Bring your strings"),
                t("marketing.loop_band.bring_sources_description", "Push from the CLI or import a file. Keep the formats your app already uses."))
            WorkflowStage(2, t("marketing.loop_band.create_drafts", "Create draft translations"),
                t("marketing.loop_band.create_drafts_description", "Write translations yourself or let an agent propose them with your project context."))
            WorkflowStage(3, t("marketing.loop_band.review_separately", "Review before approval"),
                t("marketing.loop_band.review_separately_description", "Inspect the source and suggested wording. Accept agent drafts, then approve translations separately."))
            WorkflowStage(4, t("marketing.loop_band.export_to_app", "Export into your app"),
                t("marketing.loop_band.export_to_app_description", "Pull approved translations, inspect the diff and run the checks configured for your release."))
        }
        DocsProse { DocsInlineLink("/docs/cli#start", t("marketing.loop_band.cli_setup", "Follow the CLI setup")) }
    }
}

@Composable
private fun WorkflowStage(number: Int, heading: String, description: String) {
    Li(LoopStageStyle.toModifier().toAttrs()) {
        Column(LoopCardStyle.toModifier().gap(Space.MD)) {
            Div(LoopChipStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) { Text(number.toString()) }
            H3(Modifier.displayFont().fontSize(TypeScale.HeadingSmall).fontWeight(700)
                .lineHeight(TypeScale.BodyLineHeight).margin(0.px).toAttrs()) { Text(heading) }
            P(Modifier.fontSize(TypeScale.BodySmall).lineHeight(TypeScale.BodyLineHeight)
                .color(ColorMode.current.toSitePalette().textSecondary).margin(0.px).toAttrs()) { Text(description) }
        }
    }
}

val LoopPipelineStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().gap(Space.XXXL).margin(0.px).padding(0.px).styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0, 1fr)")
            property("list-style", "none")
        }
    }
    Breakpoint.SM { Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") } }
    Breakpoint.LG { Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") } }
}

val LoopStageStyle = CssStyle.base { Modifier.styleModifier { property("min-width", "0") } }
val LoopCardStyle = CssStyle.base { Modifier.fillMaxWidth().padding(topBottom = Space.MD) }
val LoopChipStyle = CssStyle.base {
    Modifier.monoFont().fontSize(TypeScale.BodySmall).fontWeight(700).color(colorMode.toSitePalette().accent)
}
